import { useActiveAddress } from "@arweave-wallet-kit/react"
import styled from "@emotion/styled"

import { Tooltip, Typography } from "@mui/material"

import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"

import { RichTokenInfo } from "../../ao/router"
import { DAI, stETH } from "../../settings"
import { NumberFont } from "../../theme"
import { parseBigIntAsNumber, formatNumberAuto, formatTicker } from "../../utils/format"
import Button from "../Button"
import TokenLogo from "../TokenLogo"

// Placeholder for InfoOutlinedIcon
const InfoOutlinedIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
)

// Placeholder for Skeleton component
const Skeleton = ({ width, height }: { width: number; height: number }) => (
  <div
    style={{
      width: `${width}px`,
      height: `${height}px`,
      backgroundColor: "var(--mui-palette-action-hover)",
      borderRadius: "4px",
      animation: "pulse 1.5s ease-in-out infinite",
    }}
  />
)

interface DelegationCapacityProps {
  depositedBalance: {
    dai: bigint
    stEth: bigint
  }
  existingBotegaFactor: number
  isLoading: boolean
  delegationMutation: any
  changeViewToDeposit: () => void
  ethAddress?: string | null
  connectEthWallet: () => void
}

type RichTokenInfoExt = {
  idx: string
  approved: boolean
} & RichTokenInfo

export default function DelegationCapacity({
  depositedBalance,
  existingBotegaFactor,
  isLoading,
  delegationMutation,
  changeViewToDeposit,
  ethAddress,
  connectEthWallet,
}: DelegationCapacityProps) {
  const activeAddress = useActiveAddress()

  const { data: arBalance } = useQuery({
    queryKey: ["arBalance", activeAddress],
    queryFn: async () => {
      const res = await fetch(`https://arweave.net/wallet/${activeAddress}/balance`)

      if (!res.ok) {
        throw new Error("Failed to fetch balance")
      }

      const balance = await res.text()

      return BigInt(balance)
    },
    enabled: !!activeAddress,
  })

  const tokens: RichTokenInfoExt[] = useMemo(
    () => [
      {
        idx: "dai",
        ...DAI,
        approved: true,
      },
      {
        idx: "stEth",
        ...stETH,
        approved: true,
      },
    ],
    [],
  )

  // Get total deposited amounts
  const hasDeposits = useMemo(() => {
    return (
      BigInt(depositedBalance.dai) > 0n ||
      BigInt(depositedBalance.stEth) > 0n ||
      (arBalance ?? 0n) > 0n
    )
  }, [depositedBalance, arBalance])

  return (
    <CapacityWrapper>
      <GridContainer>
        <div>
          <StatsCard className={hasDeposits ? "has-deposits" : ""}>
            <StatCardContent>
              <CardHeader>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Tooltip title="Increase the Delegation Capacity by Depositing into the AO Network.">
                    <span style={{ display: "inline-flex", opacity: 0.7 }}>
                      <InfoOutlinedIcon />
                    </span>
                  </Tooltip>
                  <Typography variant="subtitle2" color="text.secondary">
                    Deposited Assets
                  </Typography>
                </div>
              </CardHeader>

              {isLoading ? (
                <Skeleton width={120} height={40} />
              ) : (
                <TokensList>
                  <TokenItem>
                    <TokenLogo tx={""} alt={`Arweave logo`} size="small" />
                    <TokenValue isEmpty={arBalance === 0n}>
                      {formatNumberAuto(parseBigIntAsNumber(arBalance, 12))} {formatTicker("AR")}
                    </TokenValue>
                  </TokenItem>
                  {tokens.map((token) => {
                    const value = depositedBalance[token.idx as keyof typeof depositedBalance]
                    const amount = parseBigIntAsNumber(value, token.denomination)

                    return (
                      <TokenItem key={token.idx}>
                        <TokenLogo tx={token?.logo} alt={`${token?.name} logo`} size="small" />
                        <TokenValue isEmpty={BigInt(value) === 0n}>
                          {formatNumberAuto(amount)} {formatTicker(token?.ticker)}
                        </TokenValue>
                      </TokenItem>
                    )
                  })}
                </TokensList>
              )}

              {!ethAddress ? (
                <ContinueButton
                  className="accent"
                  onClick={connectEthWallet}
                  style={{ maxHeight: "40px", marginTop: "auto" }}
                >
                  Connect Ethereum Wallet
                </ContinueButton>
              ) : (
                <ContinueButton
                  className="accent"
                  onClick={changeViewToDeposit}
                  style={{ marginTop: "auto", maxHeight: "40px" }}
                >
                  {hasDeposits ? "Deposit More" : "Deposit Assets"}
                </ContinueButton>
              )}
            </StatCardContent>
          </StatsCard>
        </div>

        <div>
          <StatsCard>
            <StatCardContent>
              <CardHeader>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Tooltip title="Delegation Capacity indicates the amount of AO available for delegation from your connected AO address.">
                    <span style={{ display: "inline-flex", opacity: 0.7 }}>
                      <InfoOutlinedIcon />
                    </span>
                  </Tooltip>
                  <Typography variant="subtitle2" color="text.secondary">
                    Delegated to Botega
                  </Typography>
                </div>
                <DelegationValue>{existingBotegaFactor / 100}%</DelegationValue>
              </CardHeader>

              {isLoading ? (
                <Skeleton width={120} height={40} />
              ) : !hasDeposits ? (
                <EmptyStateMessage>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No assets deposited yet. Deposit assets first to enable delegation to Botega.
                  </Typography>
                  <EarningsInfo>
                    <InfoIcon>💡</InfoIcon>
                    <span>Delegate AO to Botega to earn $BOTG governance tokens</span>
                  </EarningsInfo>
                </EmptyStateMessage>
              ) : (
                <>
                  <DelegationDisplay>
                    <DelegationProgress>
                      <ProgressMeter>
                        <ProgressBar percentage={existingBotegaFactor / 100} />
                        <ProgressLabels>
                          <ProgressLabel>0%</ProgressLabel>
                          <ProgressLabel>50%</ProgressLabel>
                          <ProgressLabel>100%</ProgressLabel>
                        </ProgressLabels>
                      </ProgressMeter>
                    </DelegationProgress>
                  </DelegationDisplay>
                  {existingBotegaFactor !== 10000 && (
                    <EarningsInfo>
                      <>
                        <InfoIcon>💡</InfoIcon>
                        <span>Set to 100% to maximize $BOTG earnings</span>
                      </>
                    </EarningsInfo>
                  )}
                </>
              )}

              {existingBotegaFactor === 10000 ? (
                <ButtonGroup>
                  <SuccessButton disabled>
                    <InfoIcon>🎯</InfoIcon>
                    <span>Maximum BOTG earnings activated!</span>
                  </SuccessButton>
                </ButtonGroup>
              ) : hasDeposits ? (
                <ButtonGroup>
                  <ContinueButton
                    className="accent"
                    disabled={existingBotegaFactor === 10000 || delegationMutation.isPending}
                    onClick={() => delegationMutation.mutate(existingBotegaFactor / 100 + 5)}
                  >
                    {delegationMutation.isPending
                      ? "Updating..."
                      : `+5% (to ${Math.min(100, existingBotegaFactor / 100 + 5)}%)`}
                  </ContinueButton>
                  <MaxButton
                    className="accent"
                    disabled={existingBotegaFactor === 10000 || delegationMutation.isPending}
                    onClick={() => delegationMutation.mutate(100)}
                  >
                    MAX
                  </MaxButton>
                </ButtonGroup>
              ) : (
                <ContinueButton className="accent" onClick={changeViewToDeposit}>
                  Deposit Assets First
                </ContinueButton>
              )}
            </StatCardContent>
          </StatsCard>
        </div>
      </GridContainer>
    </CapacityWrapper>
  )
}

const CapacityWrapper = styled.div`
  width: 100%;
  border-radius: 0;
  border-top: 0;
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

// Styled components matching StatsPanel style
const StatsCard = styled.div`
  padding: 1.25rem;
  height: 100%;
  border: 1px solid var(--mui-palette-divider);
  border-radius: var(--mui-shape-borderRadius);
  transition: all 0.2s ease;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

  [data-mui-color-scheme="dark"] & {
    background-color: var(--mui-palette-background-paper);
  }

  &.has-deposits {
    border-color: rgba(var(--theme-color), 0.3);
    background-color: rgba(var(--theme-color), 0.05);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`

const StatCardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 1rem;
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const TokensList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 0.5rem 0;
`

const TokenItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TokenValue = styled.div<{ isEmpty: boolean }>`
  font-family: ${NumberFont};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${(props) =>
    props.isEmpty ? "var(--mui-palette-text-disabled)" : "var(--mui-palette-text-primary)"};

  ${(props) => props.isEmpty && "font-style: italic;"}
`

const DelegationDisplay = styled.div`
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
`

const DelegationProgress = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.75rem;
`

const ProgressMeter = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.25rem;
`

const ProgressBar = styled.div<{ percentage: number }>`
  height: 8px;
  width: 100%;
  background-color: var(--mui-palette-action-disabledBackground);
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    height: 100%;
    width: ${(props) => props.percentage}%;
    left: 0;
    top: 0;
    background-color: rgb(var(--theme-color));
    border-radius: 4px;
    transition: width 0.5s ease;
  }
`

const ProgressLabels = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const ProgressLabel = styled.div`
  font-size: 0.7rem;
  color: var(--mui-palette-text-secondary);
  font-family: ${NumberFont};
`

const DelegationValue = styled.div`
  font-family: ${NumberFont};
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--mui-palette-text-primary);
`

const EarningsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: rgba(var(--theme-color), 0.08);
  color: rgb(var(--theme-color));
  margin: 0.25rem 0;

  span {
    line-height: 1.2;
  }
`

const InfoIcon = styled.span`
  font-size: 1rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
  width: 100%;
`

const ContinueButton = styled(Button)`
  font-size: 0.875rem;
  padding: 0.625rem 1rem;
  transition: all 0.2s ease;
  flex: 1;

  &:hover {
    transform: translateY(-2px);
  }

  &.accent {
    background-color: rgb(var(--theme-color));
    color: white;
  }
`

const MaxButton = styled(Button)`
  font-size: 0.875rem;
  padding: 0.625rem 0.75rem;
  min-width: 60px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`

const SuccessButton = styled(ContinueButton)`
  background-color: var(--mui-palette-secondary-main);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  width: 100%;

  &:disabled {
    background-color: var(--mui-palette-secondary-main);
    opacity: 0.9;
    color: white;
  }

  &:hover {
    opacity: 1;
    transform: translateY(-2px);
  }
`

const EmptyStateMessage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
`
